import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Users, FileText, CheckCircle, XCircle, Loader2, 
  AlertTriangle, TrendingUp, Briefcase, BarChart3, ArrowRight
} from 'lucide-react';
import { getStatusBadgeClass, getStatusLabel, formatDate } from '../../lib/utils';
import { api } from '../../lib/api';
import OnboardingKPIs from '../../components/admin/OnboardingKPIs';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, gapsRes] = await Promise.all([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/dashboard/recruitment-gaps')
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (gapsRes.ok) setGaps(await gapsRes.json());
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const getGapColor = (status) => {
    switch (status) {
      case 'deficit': return 'text-red-600 bg-red-50 border-red-200';
      case 'equilibre': return 'text-tp-accent bg-tp-accent/10 border-tp-accent/20';
      case 'surplus': return 'text-tp-primary bg-tp-primary/10 border-tp-primary/20';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getGapIcon = (status) => {
    switch (status) {
      case 'deficit': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'equilibre': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'surplus': return <TrendingUp className="h-4 w-4 text-blue-500" />;
      default: return <Briefcase className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="dashboard-page">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-display font-semibold mb-1 md:mb-2 text-tp-text">Tableau de bord</h1>
        <p className="text-tp-text-light text-sm md:text-base">Vue d'ensemble du pipeline de recrutement</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <Card className="p-3 md:p-6 border-tp-border">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2 md:p-3 rounded-lg bg-tp-primary/10">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-tp-primary" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-tp-text-light">Total candidats</p>
              <p className="text-xl md:text-2xl font-display font-semibold text-tp-text">{stats?.total_candidates || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-6 border-tp-border">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2 md:p-3 rounded-lg bg-[#0F2744]/10">
              <FileText className="h-5 w-5 md:h-6 md:w-6 text-[#0F2744]" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-tp-text-light">Actifs</p>
              <p className="text-xl md:text-2xl font-display font-semibold text-tp-text">{stats?.active_candidates || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-6 border-tp-border">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2 md:p-3 rounded-lg bg-tp-success/10">
              <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-tp-success" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-tp-text-light">Validés</p>
              <p className="text-xl md:text-2xl font-display font-semibold text-tp-text">{stats?.validated || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-6 border-tp-border">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2 md:p-3 rounded-lg bg-[#8B2020]/10">
              <XCircle className="h-5 w-5 md:h-6 md:w-6 text-[#8B2020]" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-tp-text-light">Rejetés</p>
              <p className="text-xl md:text-2xl font-display font-semibold text-tp-text">{stats?.rejected || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Onboarding KPIs */}
      <div className="mb-6 md:mb-8">
        <OnboardingKPIs days={30} />
      </div>

      {/* Recruitment Gaps Analysis */}
      {gaps && gaps.gaps?.length > 0 && (
        <Card className="p-4 md:p-6 border-tp-border mb-6 md:mb-8" data-testid="recruitment-gaps-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-tp-accent" />
              <h2 className="text-base md:text-lg font-display font-semibold text-tp-text">Scoring recrutement</h2>
            </div>
            {gaps.summary?.categories_in_deficit > 0 && (
              <Badge className="bg-red-100 text-red-700 border-0">
                {gaps.summary.categories_in_deficit} déficit{gaps.summary.categories_in_deficit > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          {/* Summary bar */}
          <div className="grid grid-cols-3 gap-3 mb-5 p-3 bg-tp-bg rounded-lg">
            <div className="text-center">
              <p className="text-lg md:text-xl font-display font-bold text-tp-text">{gaps.summary?.total_candidates || 0}</p>
              <p className="text-xs text-tp-text-light">Candidats</p>
            </div>
            <div className="text-center">
              <p className="text-lg md:text-xl font-display font-bold text-tp-accent">{gaps.summary?.total_qualified || 0}</p>
              <p className="text-xs text-tp-text-light">Qualifiés</p>
            </div>
            <div className="text-center">
              <p className="text-lg md:text-xl font-display font-bold text-tp-primary">{gaps.summary?.total_open_missions || 0}</p>
              <p className="text-xs text-tp-text-light">Missions ouvertes</p>
            </div>
          </div>

          {/* Gap items */}
          <div className="space-y-3">
            {gaps.gaps.map((gap, index) => (
              <div 
                key={`gap-${gap.category}`} 
                className={`p-3 rounded-lg border ${getGapColor(gap.status)}`}
                data-testid={`gap-item-${gap.category}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getGapIcon(gap.status)}
                    <span className="font-medium text-sm">{gap.category}</span>
                  </div>
                  <span className="text-xs font-medium">{gap.status_label}</span>
                </div>
                
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Candidats</span>
                    <p className="font-semibold">{gap.candidates_total}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Qualifiés</span>
                    <p className="font-semibold">{gap.candidates_qualified}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Missions</span>
                    <p className="font-semibold">{gap.open_missions}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Demande totale</span>
                    <p className="font-semibold">{gap.total_demand_history}</p>
                  </div>
                </div>
                
                {gap.candidates_total > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Taux qualification</span>
                      <span>{gap.candidates_total > 0 ? Math.round((gap.candidates_qualified / gap.candidates_total) * 100) : 0}%</span>
                    </div>
                    <Progress value={gap.candidates_total > 0 ? (gap.candidates_qualified / gap.candidates_total) * 100 : 0} className="h-1.5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Candidates */}
      <Card className="p-4 md:p-6 border-tp-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base md:text-lg font-display font-semibold text-tp-text">Candidats récents</h2>
          <Link to="/admin/candidates" className="text-xs text-tp-primary hover:underline flex items-center gap-1">
            Voir tous <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="space-y-3">
          {stats?.recent_candidates?.length > 0 ? (
            stats.recent_candidates.map((candidate) => (
              <Link
                key={candidate.id}
                to={`/admin/candidates/${candidate.id}`}
                className="block p-3 md:p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm md:text-base truncate">{candidate.first_name} {candidate.last_name}</p>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">
                      {candidate.main_profession || candidate.primary_category || 'Non défini'} • {candidate.country}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={getStatusBadgeClass(candidate.status)}>
                      {getStatusLabel(candidate.status)}
                    </Badge>
                    <span className="text-xs text-muted-foreground hidden sm:block">{formatDate(candidate.created_at)}</span>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Aucun candidat pour l'instant</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DashboardPage;
